// Provenance: repo:tds-reconciliation-portal:frontend/src/components/TablePager.tsx
// (ADR-0005) — restyled on the estate tokens (ADR-0010); the dense/one-band form carries.
import type { ReactNode } from 'react'

const SIZES = [25, 50, 100, 250, 500] // backend clamps at 500
const fmt = new Intl.NumberFormat('en-IN')

/** Records-per-page + page controls with a "showing X–Y of N" readout.
 *  Rendered above and below long tables so paging is reachable either way.
 *
 *  `dense` is the in-band form: it shares a control row with the filters, so
 *  the prose shrinks to "X–Y of N" — every control still present, at roughly
 *  half the width. */
export function TablePager({ total, page, size, onPage, onSize, compact = false,
  dense = false, middle }: {
  total: number
  page: number
  size: number
  onPage: (p: number) => void
  onSize: (s: number) => void
  compact?: boolean
  dense?: boolean
  /** controls that share the pager's row (view toggles, search…) — the
   *  density pass's one-band rule: controls live where the data is */
  middle?: ReactNode
}) {
  const pages = Math.max(1, Math.ceil(total / size))
  const from = total === 0 ? 0 : (page - 1) * size + 1
  const to = Math.min(total, page * size)

  const sizeSelect = (
    <select
      className="rounded-md border border-border bg-background px-1.5 py-1 text-xs"
      value={size}
      onChange={(e) => {
        onSize(Number(e.target.value))
        onPage(1)
      }}
      aria-label="Records per page"
    >
      {SIZES.map((s) => (
        <option key={s} value={s}>{s}</option>
      ))}
    </select>
  )

  const pageButtons = pages > 1 && (
    <span className="inline-flex items-center gap-1">
      <button
        className="rounded border border-border px-2 py-0.5 text-xs disabled:opacity-40"
        disabled={page <= 1}
        onClick={() => onPage(page - 1)}
      >
        ‹
      </button>
      <span className="px-1 text-xs text-muted-foreground">
        {fmt.format(page)} / {fmt.format(pages)}
      </span>
      <button
        className="rounded border border-border px-2 py-0.5 text-xs disabled:opacity-40"
        disabled={page >= pages}
        onClick={() => onPage(page + 1)}
      >
        ›
      </button>
    </span>
  )

  if (dense) {
    return (
      <div className="flex items-center gap-2">
        <span className="whitespace-nowrap text-xs text-muted-foreground">
          {fmt.format(from)}–{fmt.format(to)} of {fmt.format(total)}
        </span>
        {sizeSelect}
        {pageButtons}
      </div>
    )
  }

  return (
    <div className="flex flex-wrap items-center justify-between gap-2">
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <span>
          Showing {fmt.format(from)}–{fmt.format(to)} of {fmt.format(total)}
        </span>
        {!compact && (
          <>
            <span>·</span>
            {sizeSelect}
            <span>per page</span>
          </>
        )}
      </div>
      {middle && <div className="flex min-w-0 flex-1 items-center gap-2">{middle}</div>}
      {pageButtons}
    </div>
  )
}
